import Module from 'node:module'
import path from 'node:path'
import { fileURLToPath } from 'node:url'
import { parentPort } from 'node:worker_threads'
import chokidar from 'chokidar'
import type { ESLint } from 'eslint'
import invariant from 'tiny-invariant'

import { Checker } from '../../Checker.js'
import { FileDiagnosticManager } from '../../FileDiagnosticManager.js'
import { createIgnore } from '../../glob.js'
import {
  composeCheckerSummary,
  consoleLog,
  diagnosticToConsoleLevel,
  diagnosticToRuntimeError,
  diagnosticToTerminalLog,
  filterLogLevel,
  normalizeEslintDiagnostic,
  toClientPayload,
} from '../../logger.js'
import { ACTION_TYPES, DiagnosticLevel } from '../../types.js'
import { ignoreTransientFsError } from '../../utils.js'
import { applyBatchedDiagnostics } from '../_shared/applyBatchedDiagnostics.js'
import {
  createLintScheduler,
  DEFAULT_DEBOUNCE_MS,
} from '../_shared/lintScheduler.js'
import { translateOptions } from './cli.js'
import { options as optionator } from './options.js'

const __filename = fileURLToPath(import.meta.url)
const _require = Module.createRequire(import.meta.url)

const manager = new FileDiagnosticManager()

let createServeAndBuild: any

import type { CreateDiagnostic } from '../../types'

/**
 * Detect the installed ESLint major version.
 */
function getEslintMajorVersion(): number {
  try {
    const eslintPkg = _require('eslint/package.json')
    return Number.parseInt(eslintPkg.version.split('.')[0], 10)
  } catch {
    return 10 // assume latest if we can't detect
  }
}

/**
 * Resolve the correct ESLint class based on version and config mode.
 *
 * - ESLint v10+: Only flat config is supported, use `ESLint` directly.
 * - ESLint v9 with flat config (default): Use `ESLint` directly (it's the flat config class in v9).
 * - ESLint v9 with legacy eslintrc: Use `LegacyESLint` from `eslint/use-at-your-own-risk`.
 */
async function resolveEslintClass(
  useFlatConfig: boolean,
  majorVersion: number,
): Promise<typeof ESLint> {
  const { ESLint } = await import('eslint')
  if (majorVersion >= 10) {
    // v10+ only supports flat config
    return ESLint
  }

  if (useFlatConfig) {
    // v9 with flat config — the default `ESLint` export is already the flat config class
    return ESLint
  }

  // v9 with legacy eslintrc — need to load LegacyESLint
  try {
    const { LegacyESLint } = _require('eslint/use-at-your-own-risk')
    if (LegacyESLint) {
      return LegacyESLint
    }
  } catch {
    // fall through
  }

  throw new Error(
    'Legacy eslintrc mode (useFlatConfig: false) requires ESLint v9. ' +
      'ESLint v10+ only supports flat config. Please upgrade your config to flat config ' +
      'or set useFlatConfig to true.',
  )
}

const createDiagnostic: CreateDiagnostic<'eslint'> = (pluginConfig) => {
  let overlay = true
  let terminal = true

  return {
    config: async ({ enableOverlay, enableTerminal }) => {
      overlay = enableOverlay
      terminal = enableTerminal
    },
    async configureServer({ root }) {
      if (!pluginConfig.eslint) return

      const majorVersion = getEslintMajorVersion()
      const useFlatConfig = pluginConfig.eslint.useFlatConfig ?? true

      if (!useFlatConfig && majorVersion >= 10) {
        console.warn(
          '[vite-plugin-checker] useFlatConfig: false is not supported with ESLint v10+. ' +
            'Falling back to flat config mode.',
        )
      }

      const effectiveUseFlatConfig = majorVersion >= 10 ? true : useFlatConfig

      const options = optionator.parse(pluginConfig.eslint.lintCommand)
      invariant(
        !options.fix,
        'Using `--fix` in `config.eslint.lintCommand` is not allowed in vite-plugin-checker, you could using `--fix` with editor.',
      )

      const translatedOptions = translateOptions(
        options,
        effectiveUseFlatConfig,
      ) as ESLint.Options

      const logLevel = (() => {
        if (typeof pluginConfig.eslint !== 'object') return undefined
        const userLogLevel = pluginConfig.eslint.dev?.logLevel
        if (!userLogLevel) return undefined
        const map = {
          error: DiagnosticLevel.Error,
          warning: DiagnosticLevel.Warning,
        } as const

        return userLogLevel.map((l) => map[l])
      })()

      const eslintOptions: ESLint.Options = {
        cwd: root,
        ...translatedOptions,
        ...pluginConfig.eslint.dev?.overrideConfig,
      }

      const EslintClass = await resolveEslintClass(
        effectiveUseFlatConfig,
        majorVersion,
      )
      const eslint = new EslintClass(eslintOptions)

      const dispatchDiagnostics = () => {
        const diagnostics = filterLogLevel(manager.getDiagnostics(), logLevel)
        if (terminal) {
          for (const d of diagnostics) {
            consoleLog(
              diagnosticToTerminalLog(d, 'ESLint'),
              diagnosticToConsoleLevel(d),
            )
          }

          const errorCount = diagnostics.filter(
            (d) => d.level === DiagnosticLevel.Error,
          ).length
          const warningCount = diagnostics.filter(
            (d) => d.level === DiagnosticLevel.Warning,
          ).length
          consoleLog(
            composeCheckerSummary('ESLint', errorCount, warningCount),
            errorCount ? 'error' : warningCount ? 'warn' : 'info',
          )
        }

        if (overlay) {
          parentPort?.postMessage({
            type: ACTION_TYPES.overlayError,
            payload: toClientPayload(
              'eslint',
              diagnostics.map((d) => diagnosticToRuntimeError(d)),
            ),
          })
        }
      }

      const scheduler = createLintScheduler({
        debounceMs: pluginConfig.eslint.dev?.debounceMs ?? DEFAULT_DEBOUNCE_MS,
        onBatch: async (files) => {
          const diagnosticsOfChangedFiles = await eslint.lintFiles(files)
          const newDiagnostics = diagnosticsOfChangedFiles.flatMap((d) =>
            normalizeEslintDiagnostic(d),
          )
          applyBatchedDiagnostics(manager, files, newDiagnostics, root)
          dispatchDiagnostics()
        },
      })

      const shouldLintPath = async (absPath: string): Promise<boolean> => {
        // Legacy-mode extension filter (only meaningful if --ext was provided).
        if (!effectiveUseFlatConfig) {
          const extension = path.extname(absPath)
          const { extensions } = eslintOptions as any
          const hasExtensionsConfig = Array.isArray(extensions)
          if (hasExtensionsConfig && !extensions.includes(extension)) {
            return false
          }
        }
        // Honor .eslintignore / ignorePatterns.
        const isIgnored = await eslint.isPathIgnored(absPath)
        return !isIgnored
      }

      // initial lint
      const files = options._.slice(1) as string[]
      const diagnostics = await eslint.lintFiles(files)

      manager.initWith(diagnostics.flatMap((p) => normalizeEslintDiagnostic(p)))
      dispatchDiagnostics()

      // watch lint
      let watchTarget: string | string[] = root
      if (pluginConfig.eslint.watchPath) {
        if (Array.isArray(pluginConfig.eslint.watchPath)) {
          watchTarget = pluginConfig.eslint.watchPath.map((p) =>
            path.resolve(root, p),
          )
        } else {
          watchTarget = path.resolve(root, pluginConfig.eslint.watchPath)
        }
      }

      const watcher = chokidar.watch(watchTarget, {
        cwd: root,
        ignored: createIgnore(root, files),
      })

      watcher.on('change', (filePath) => {
        const absPath = path.resolve(root, filePath)
        shouldLintPath(absPath)
          .then((shouldLint) => {
            if (shouldLint) scheduler.schedule(absPath)
          })
          .catch(ignoreTransientFsError)
      })
      watcher.on('unlink', (filePath) => {
        const absPath = path.resolve(root, filePath)
        manager.updateByFileId(absPath, [])
        dispatchDiagnostics()
      })
    },
  }
}

export class EslintChecker extends Checker<'eslint'> {
  public constructor() {
    super({
      name: 'eslint',
      absFilePath: __filename,
      build: {
        buildBin: (pluginConfig) => {
          if (pluginConfig.eslint) {
            const { lintCommand } = pluginConfig.eslint
            return ['eslint', lintCommand.split(' ').slice(1)]
          }
          return ['eslint', ['']]
        },
      },
      createDiagnostic,
    })
  }

  public init() {
    const _createServeAndBuild = super.initMainThread()
    createServeAndBuild = _createServeAndBuild
    super.initWorkerThread()
  }
}

export { createServeAndBuild }

const eslintChecker = new EslintChecker()
eslintChecker.prepare()
eslintChecker.init()
